import {
  TableRow,
  TableCell,
  Typography,
  Stack,
  Box,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AnyPlan, CountryTotals } from '../../types';
import { isDifferentFromBase } from './helpers';
import { formatBudget } from '../../helpers';

type CountrySummaryRowProps = {
  country: string;
  plans: AnyPlan[];
  firstColWidth: number;
  getCountryTotals: (plan: AnyPlan, country: string) => CountryTotals;
  expanded: boolean;
  onToggle: () => void;
};

export default function CountrySummarySection({
  country,
  plans,
  firstColWidth,
  getCountryTotals,
  expanded,
  onToggle,
}: CountrySummaryRowProps) {
  const countryTotals = plans.map((plan) => getCountryTotals(plan, country));
  const totalBudgets = countryTotals.map((t) => t.totalBudget);
  const techCosts = countryTotals.map((t) => t.techCosts);
  const oneReaches = countryTotals.map((t) => t.oneReach);

  return (
    <TableRow hover sx={{ cursor: 'pointer' }} onClick={onToggle}>
      <TableCell
        sx={{
          width: firstColWidth,
          fontWeight: 700,
          borderRight: '1px solid',
          borderColor: '#C7D8F9',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
          <Typography fontWeight={500} fontSize="16px">
            {country}
          </Typography>
        </Stack>
      </TableCell>

      {plans.map((plan, index) => {
        const t = countryTotals[index];

        const budgetDiff = isDifferentFromBase<number>(totalBudgets, index);
        const techDiff = isDifferentFromBase<number>(techCosts, index);
        const oneReachDiff = isDifferentFromBase<number | undefined>(
          oneReaches,
          index
        );

        return (
          <TableCell
            key={`ctot-${plan.id}`}
            sx={{
              borderLeft: '1px solid',
              borderColor: '#C7D8F9',
              p: 0,
              verticalAlign: 'middle',
            }}
          >
            <Box sx={{ display: 'table', height: '100%', width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto auto',
                    columnGap: 2,
                    rowGap: 0.5,
                  }}
                >
                  <Typography fontSize="12px" lineHeight="24px">
                    TOTAL BUDGET:
                  </Typography>
                  <Typography
                    fontWeight={600}
                    fontSize="16px"
                    textAlign="right"
                    sx={{ color: budgetDiff ? 'error.main' : '#525F81' }}
                  >
                    {formatBudget(t.totalBudget, t.currency)}
                  </Typography>

                  <Typography fontSize="10px" lineHeight="24px">
                    TECH COSTS:
                  </Typography>
                  <Typography
                    fontWeight={500}
                    fontSize="14px"
                    textAlign="right"
                    sx={{ color: techDiff ? 'error.main' : '#525F81' }}
                  >
                    {formatBudget(t.techCosts, t.currency)}
                  </Typography>

                  <Typography fontSize="10px" lineHeight="24px">
                    ONE REACH:
                  </Typography>
                  <Typography
                    fontSize="12px"
                    textAlign="right"
                    sx={{ color: oneReachDiff ? 'error.main' : '#525F81' }}
                  >
                    {t.oneReach != null
                      ? `${Math.round(t.oneReach * 100)}%`
                      : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </TableCell>
        );
      })}
    </TableRow>
  );
}
